// Portfolio Administration v1.0 PR 5 — Qualifications tab on the Local
// Enforcer registry detail page. Renders bar admissions (state-by-state
// for law-firm enforcers), liability + E&O insurance status, W-9/MSA
// flags, compliance certifications, and the re-qualification countdown.
//
// Per spec §9.2 + spec §14: only E1 / ADMIN can record qualification
// changes. The "Re-qualify" affordance below uses the existing
// QualifyEnforcerAsync service.

import { useState } from "react";
import {
  Button,
  Card,
  Input,
  Label,
} from "@/design-system/components/ui";
import { AlertTriangle, Check, X } from "lucide-react";
import { useAuth } from "@/hooks/useAuth";
import { useQualifyLocalEnforcer } from "@/hooks/portfolioAdmin/useRegistries";
import type { LocalEnforcerDetailDto } from "@/api/portfolioAdmin/pr4bTypes";
import {
  calendarDaysUntil as daysUntil,
  formatCalendarDate,
  isoDateInTz,
  useDateFormatter,
} from "@/utils/datetime";

function parseJsonArray<T = unknown>(raw: string | null): T[] {
  if (!raw) return [];
  try {
    const parsed = JSON.parse(raw);
    return Array.isArray(parsed) ? (parsed as T[]) : [];
  } catch {
    return [];
  }
}

interface BarAdmission {
  state: string;
  status: string;
  barNumber?: string;
  expirationDate?: string;
}

export function EnforcerQualificationsTab({
  enforcer,
}: {
  enforcer: LocalEnforcerDetailDto;
}) {
  const auth = useAuth();
  const { tz } = useDateFormatter();
  const canQualify =
    auth.hasRoleCode("E1") ||
    auth.hasRoleCode("ADMIN") ||
    auth.hasPermission("Enforcer.Manage");
  const qualify = useQualifyLocalEnforcer(enforcer.id);
  const [qualifiedDate, setQualifiedDate] = useState(
    enforcer.qualifiedDate ?? isoDateInTz(new Date(), tz),
  );
  const [requalDate, setRequalDate] = useState(
    enforcer.requalificationDueDate ??
      isoDateInTz(new Date(Date.now() + 365 * 86_400_000), tz),
  );

  const admissions = parseJsonArray<BarAdmission>(
    enforcer.barAdmissionsVerifiedJson,
  );
  const certs = parseJsonArray<string | { name: string; expirationDate?: string }>(
    enforcer.complianceCertificationsJson,
  );

  const liabExp = daysUntil(enforcer.liabilityInsuranceExpirationDate);
  const eoExp = daysUntil(enforcer.errorsOmissionsExpirationDate);
  const requalDays = daysUntil(enforcer.requalificationDueDate);

  const submitRequalify = async (e: React.FormEvent) => {
    e.preventDefault();
    await qualify.mutateAsync({
      qualifiedDate,
      requalificationDueDate: requalDate,
    });
  };

  return (
    <div
      className="flex flex-col gap-space-3"
      data-testid="enforcer-qualifications-tab"
    >
      {enforcer.enforcerType === "law-firm" ? (
        <Card className="flex flex-col gap-space-2">
          <h3 className="font-display font-bold text-h3 m-0">Bar admissions</h3>
          {admissions.length === 0 ? (
            <p className="text-small text-gray-mid italic m-0">
              No bar admissions recorded yet.
            </p>
          ) : (
            <table
              className="w-full text-small"
              data-testid="enforcer-bar-admissions-table"
            >
              <thead>
                <tr className="text-left text-gray-mid border-b border-border-light">
                  <th className="py-[6px] font-display">State</th>
                  <th className="py-[6px] font-display">Status</th>
                  <th className="py-[6px] font-display">Bar #</th>
                  <th className="py-[6px] font-display">Expires</th>
                </tr>
              </thead>
              <tbody>
                {admissions.map((a, i) => (
                  <tr
                    key={`${a.state}-${i}`}
                    className="border-b border-border-light"
                    data-testid={`enforcer-bar-row-${a.state}`}
                  >
                    <td className="py-[6px]">{a.state}</td>
                    <td className="py-[6px]">{a.status}</td>
                    <td className="py-[6px]">{a.barNumber ?? "—"}</td>
                    <td className="py-[6px]">
                      {a.expirationDate ? formatCalendarDate(a.expirationDate) : "—"}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </Card>
      ) : null}

      <div className="grid grid-cols-1 md:grid-cols-2 gap-space-3">
        <InsuranceCard
          title="Liability insurance"
          carrier={enforcer.liabilityInsuranceCarrier}
          policy={enforcer.liabilityInsurancePolicyNumber}
          coverage={enforcer.liabilityInsuranceCoverageAmount}
          expiration={enforcer.liabilityInsuranceExpirationDate}
          daysUntilExp={liabExp}
          testId="enforcer-liability-insurance"
        />
        <InsuranceCard
          title="Errors & Omissions"
          carrier={enforcer.errorsOmissionsCarrier}
          policy={enforcer.errorsOmissionsPolicyNumber}
          coverage={enforcer.errorsOmissionsCoverageAmount}
          expiration={enforcer.errorsOmissionsExpirationDate}
          daysUntilExp={eoExp}
          testId="enforcer-eo-insurance"
        />
      </div>

      <Card className="flex flex-col gap-space-2">
        <h3 className="font-display font-bold text-h3 m-0">Compliance flags</h3>
        <dl className="grid grid-cols-[200px_1fr] gap-y-space-1 gap-x-space-3 text-small m-0">
          <dt className="text-gray-mid">W-9 on file</dt>
          <dd className="m-0">
            <BoolBadge value={enforcer.w9OnFile} />
          </dd>
          <dt className="text-gray-mid">MSA on file</dt>
          <dd className="m-0">
            <BoolBadge value={enforcer.msaOnFile} />
          </dd>
          <dt className="text-gray-mid">Conflicts check</dt>
          <dd className="m-0">{enforcer.conflictsCheckProcedure ?? "—"}</dd>
          <dt className="text-gray-mid">Compliance certs</dt>
          <dd className="m-0">
            {certs.length === 0
              ? "—"
              : certs.map((c, i) => (
                  <div key={i}>
                    {typeof c === "string"
                      ? c
                      : `${c.name}${c.expirationDate ? ` (expires ${c.expirationDate})` : ""}`}
                  </div>
                ))}
          </dd>
          <dt className="text-gray-mid">Re-qualification due</dt>
          <dd className="m-0">
            {enforcer.requalificationDueDate ? formatCalendarDate(enforcer.requalificationDueDate) : "—"}
            {requalDays !== null && requalDays <= 60 ? (
              <span className="ml-space-2 inline-flex items-center gap-space-1 text-status-warning">
                <AlertTriangle size={14} aria-hidden="true" />
                {requalDays <= 0
                  ? "Overdue"
                  : `Due in ${requalDays} days`}
              </span>
            ) : null}
          </dd>
        </dl>
      </Card>

      {canQualify ? (
        <Card className="flex flex-col gap-space-2">
          <h3 className="font-display font-bold text-h3 m-0">
            Record qualification
          </h3>
          <form
            onSubmit={submitRequalify}
            className="flex flex-wrap items-end gap-space-2"
            data-testid="enforcer-qualify-form"
          >
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="enforcer-qualified-date">Qualified date</Label>
              <Input
                id="enforcer-qualified-date"
                type="date"
                value={qualifiedDate}
                onChange={(e) => setQualifiedDate(e.target.value)}
                required
              />
            </div>
            <div className="flex flex-col gap-space-1">
              <Label htmlFor="enforcer-requal-date">
                Re-qualification due
              </Label>
              <Input
                id="enforcer-requal-date"
                type="date"
                value={requalDate}
                onChange={(e) => setRequalDate(e.target.value)}
                required
              />
            </div>
            <Button
              type="submit"
              size="sm"
              disabled={qualify.isPending}
              data-testid="enforcer-qualify-submit"
            >
              {qualify.isPending ? "Recording…" : "Record qualification"}
            </Button>
          </form>
        </Card>
      ) : null}
    </div>
  );
}

interface InsuranceCardProps {
  title: string;
  carrier: string | null;
  policy: string | null;
  coverage: number | null;
  expiration: string | null;
  daysUntilExp: number | null;
  testId: string;
}
function InsuranceCard({
  title,
  carrier,
  policy,
  coverage,
  expiration,
  daysUntilExp,
  testId,
}: InsuranceCardProps) {
  const warn = daysUntilExp !== null && daysUntilExp <= 60;
  return (
    <Card className="flex flex-col gap-space-1" data-testid={testId}>
      <h3 className="font-display font-bold text-h3 m-0">{title}</h3>
      <dl className="grid grid-cols-[120px_1fr] gap-y-space-1 gap-x-space-3 text-small m-0">
        <dt className="text-gray-mid">Carrier</dt>
        <dd className="m-0">{carrier ?? "—"}</dd>
        <dt className="text-gray-mid">Policy #</dt>
        <dd className="m-0">{policy ?? "—"}</dd>
        <dt className="text-gray-mid">Coverage</dt>
        <dd className="m-0">
          {coverage != null ? `$${coverage.toLocaleString()}` : "—"}
        </dd>
        <dt className="text-gray-mid">Expires</dt>
        <dd
          className={
            warn ? "m-0 text-status-warning" : "m-0"
          }
        >
          {expiration ? formatCalendarDate(expiration) : "—"}
          {warn ? (
            <span className="ml-space-2 inline-flex items-center gap-space-1">
              <AlertTriangle size={14} aria-hidden="true" />
              {daysUntilExp! <= 0
                ? "Expired"
                : `Renews in ${daysUntilExp} days`}
            </span>
          ) : null}
        </dd>
      </dl>
    </Card>
  );
}

function BoolBadge({ value }: { value: boolean }) {
  return value ? (
    <span className="inline-flex items-center gap-space-1 text-status-success">
      <Check size={14} aria-hidden="true" /> Yes
    </span>
  ) : (
    <span className="inline-flex items-center gap-space-1 text-status-error">
      <X size={14} aria-hidden="true" /> No
    </span>
  );
}
